import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NotificationDto, notificationsApi } from "../../api/notificationsApi";

const POLL_MS = 45_000;

function routeForNotification(n: NotificationDto): string | null {
  const clientId = n.clientId?.trim();
  if (!clientId) {
    return null;
  }
  const t = (n.type || "").trim();
  if (t === "ClientAdded") {
    return `/clients/${clientId}`;
  }
  if (t === "Task" || t === "Notification" || t === "Review" || t === "Automation") {
    return `/clients/${clientId}`;
  }
  return null;
}

const OperatorNotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const refreshCount = useCallback(async () => {
    try {
      const n = await notificationsApi.unreadCount();
      setUnread(typeof n === "number" ? n : Number(n));
    } catch {
      /* ignore */
    }
  }, []);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const list = await notificationsApi.list(false, 20);
      setItems(list);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshCount();
    const t = window.setInterval(() => void refreshCount(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadRecent();
    void refreshCount();
  }, [open, loadRecent, refreshCount]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const onPick = async (n: NotificationDto) => {
    if (!n.isRead) {
      try {
        await notificationsApi.markRead(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
        await refreshCount();
      } catch {
        /* ignore */
      }
    }
    const path = routeForNotification(n);
    setOpen(false);
    if (path) {
      navigate(path);
    }
  };

  const onMarkAllRead = async () => {
    if (unread <= 0 || markingAll) {
      return;
    }
    setMarkingAll(true);
    try {
      await notificationsApi.markAllRead();
      setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
      setUnread(0);
    } catch {
      /* ignore */
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg border border-slate-700 p-2 text-slate-300 hover:bg-slate-800 hover:text-white"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/40">
          <div className="flex items-start justify-between gap-2 border-b border-slate-800 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">Notifications</p>
              <p className="text-xs text-slate-500">{unread} unread</p>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void onMarkAllRead()}
                disabled={markingAll}
                className="shrink-0 text-sm font-medium text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
              >
                {markingAll ? "Marking…" : "Mark all read"}
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading && <p className="px-4 py-6 text-center text-sm text-slate-500">Loading…</p>}
            {!loading && items.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-slate-500">No notifications yet.</p>
            )}
            {!loading &&
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => void onPick(n)}
                  className={`w-full border-b border-slate-800/80 px-4 py-3 text-left transition hover:bg-slate-800/80 ${
                    !n.isRead ? "bg-slate-800/40" : ""
                  }`}
                >
                  <p className="text-xs font-medium text-slate-500">
                    {new Date(n.createdAt).toLocaleString()}
                  </p>
                  <p
                    className={`text-sm text-white ${!n.isRead ? "font-semibold" : "font-medium text-slate-300"}`}
                  >
                    {n.title}
                  </p>
                  <p className={`mt-1 line-clamp-2 text-xs ${!n.isRead ? "text-slate-300" : "text-slate-500"}`}>
                    {n.message}
                  </p>
                  {n.clientName && <p className="mt-1 text-xs text-blue-400">Client: {n.clientName}</p>}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorNotificationBell;
